import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Check URL params BEFORE component renders
const getInitialToken = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const impersonateToken = urlParams.get('impersonate');
  
  if (impersonateToken) {
    // Store impersonation token immediately
    localStorage.setItem('token', impersonateToken);
    localStorage.setItem('isImpersonating', 'true');
    // Clean URL
    window.history.replaceState({}, document.title, window.location.pathname);
    return impersonateToken;
  }
  
  return localStorage.getItem('token');
};

// Get stored auth type (legacy or v2)
const getStoredAuthType = () => {
  return localStorage.getItem('authType') || 'legacy';
};

// Get stored user from localStorage
const getStoredUser = () => {
  try {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser);
  const [token, setToken] = useState(getInitialToken);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(
    () => localStorage.getItem('isImpersonating') === 'true'
  );
  
  // V2 auth state
  const [authType, setAuthType] = useState(getStoredAuthType);
  const [tenants, setTenants] = useState([]);
  const [activeTenant, setActiveTenant] = useState(null);
  const [pendingTenantSelection, setPendingTenantSelection] = useState(false);
  
  // Flag to suppress logout during token refresh/transition
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  // Helper to update user and persist to localStorage
  const updateUser = useCallback((userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      localStorage.removeItem('user');
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('authType');
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    setIsImpersonating(false);
    setAuthType('legacy');
    setTenants([]);
    setActiveTenant(null);
    setPendingTenantSelection(false);
  }, []);

  // Called after user changes their password
  const clearMustChangePassword = useCallback(() => {
    setMustChangePassword(false);
    // Update user object to reflect the change
    setUser(prev => {
      const updated = prev ? { ...prev, must_change_password: false } : null;
      if (updated) localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Refresh user data from backend (call after profile/company updates)
  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      // Use appropriate endpoint based on auth type
      const endpoint = authType === 'v2' ? '/v2/auth/my-tenants' : '/auth/me';
      const response = await axios.get(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (authType === 'v2') {
        setTenants(response.data.tenants || []);
        setActiveTenant(response.data.active_tenant);
        if (response.data.user) {
          updateUser(response.data.user);
        }
      } else {
        updateUser(response.data);
        setMustChangePassword(response.data.must_change_password || false);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, [token, authType, updateUser]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          // Try v2 auth first if that's what was used
          if (authType === 'v2') {
            const response = await axios.get(`${API_URL}/v2/auth/my-tenants`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setTenants(response.data.tenants || []);
            setActiveTenant(response.data.active_tenant);
            // Also set the user from the response if available
            if (response.data.user) {
              updateUser(response.data.user);
              setMustChangePassword(response.data.user.must_change_password || false);
            } else if (response.data.active_tenant) {
              // Fallback: construct user from active tenant info
              updateUser({
                id: response.data.active_tenant.user_id,
                email: response.data.active_tenant.email,
                full_name: response.data.active_tenant.full_name,
                role: response.data.active_tenant.role,
                client_id: response.data.active_tenant.client_id,
                industry: response.data.active_tenant.industry,
              });
            }
          } else {
            // Legacy auth
            const response = await axios.get(`${API_URL}/auth/me`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            updateUser(response.data);
            setMustChangePassword(response.data.must_change_password || false);
          }
        } catch (error) {
          // If v2 fails, try legacy
          if (authType === 'v2') {
            try {
              const response = await axios.get(`${API_URL}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              updateUser(response.data);
              setAuthType('legacy');
              localStorage.setItem('authType', 'legacy');
            } catch {
              logout();
            }
          } else {
            logout();
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token, authType, logout, updateUser]);

  // Legacy login (Client ID + Username + Password)
  const login = async (clientId, username, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      client_id: clientId,
      username,
      password
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('authType', 'legacy');
    localStorage.removeItem('isImpersonating');
    setToken(access_token);
    updateUser(userData);
    setAuthType('legacy');
    setMustChangePassword(userData.must_change_password || false);
    setIsImpersonating(false);
    return userData;
  };

  // V2 Login (Email + Password)
  const loginV2 = async (email, password) => {
    const response = await axios.post(`${API_URL}/v2/auth/login`, {
      email,
      password
    });
    const { access_token, user: userData, tenants: userTenants, requires_tenant_selection } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('authType', 'v2');
    localStorage.removeItem('isImpersonating');
    
    setToken(access_token);
    updateUser(userData);
    setAuthType('v2');
    setTenants(userTenants || []);
    setActiveTenant(userData.active_tenant);
    setMustChangePassword(userData.must_change_password || false);
    setIsImpersonating(false);
    setPendingTenantSelection(requires_tenant_selection);
    
    return {
      user: userData,
      tenants: userTenants,
      requiresTenantSelection: requires_tenant_selection
    };
  };

  // V2 Select Tenant (for multi-tenant users)
  const selectTenant = async (clientId) => {
    // Suppress logout on 401 during token transition
    setIsRefreshingToken(true);
    
    try {
      // Use the token from localStorage since the state token might be stale
      const currentToken = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/v2/auth/select-tenant`, 
        { client_id: clientId },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      const { access_token, user: userData, tenants: userTenants } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      updateUser(userData);
      setTenants(userTenants || []);
      setActiveTenant(userData.active_tenant);
      setPendingTenantSelection(false);
      
      return userData;
    } finally {
      // Re-enable logout on 401 after a brief delay for React to update
      setTimeout(() => setIsRefreshingToken(false), 500);
    }
  };

  const register = async (clientId, username, password, fullName, email, role) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      client_id: clientId,
      username,
      password,
      full_name: fullName,
      email,
      role
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('authType', 'legacy');
    setToken(access_token);
    updateUser(userData);
    setAuthType('legacy');
    return userData;
  };

  // Create API instance only when token changes
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    instance.interceptors.response.use(
      response => response,
      error => {
        // Don't auto-logout on 401 during token refresh/transition
        // The isRefreshingToken check uses the latest value via closure
        if (error.response?.status === 401 && !isRefreshingToken) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [token, logout, isRefreshingToken]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login,
      loginV2,
      selectTenant,
      register, 
      logout, 
      api, 
      isImpersonating,
      mustChangePassword,
      clearMustChangePassword,
      refreshUser,
      // V2 auth additions
      authType,
      tenants,
      activeTenant,
      pendingTenantSelection
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
